// Only the drag-hover overlay needs these: a dragged file exposes its MIME type but not its name,
// so it cannot be recognised by extension until it is dropped. The backend owns the allowed list.
const CHAT_FILE_MIME_TYPES: Record<string, string[]> = {
  '.pdf': ['application/pdf'],
  '.png': ['image/png'],
  '.jpg': ['image/jpeg'],
  '.jpeg': ['image/jpeg'],
  '.tiff': ['image/tiff'],
  '.bmp': ['image/bmp'],
  '.webp': ['image/webp'],
  '.docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  '.dotx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.template'],
  '.dotm': ['application/vnd.ms-word.template.macroEnabled.12'],
  '.txt': ['text/plain'],
  '.rtf': ['application/rtf', 'text/rtf'],
  '.xlsx': ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  '.csv': ['text/csv'],
};

export type ChatFileTypes = {
  extensions: string[];
  maxFileSizeBytes: number;
};

type ChatFileRejectionReason = 'type' | 'size';

export type ChatFileRejection = {
  filename: string;
  reason: ChatFileRejectionReason;
};

// Mirrors the backend's _extension_of. Browsers disagree on the MIME type of formats like .csv,
// so matching the backend on extension avoids rejecting files the server would accept.
export function chatFileExtension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot === -1 ? '' : filename.slice(dot).toLowerCase();
}

export function chatFileRejection(
  file: File,
  types: ChatFileTypes,
): ChatFileRejectionReason | null {
  if (!types.extensions.includes(chatFileExtension(file.name))) return 'type';
  if (file.size > types.maxFileSizeBytes) return 'size';
  return null;
}

export function chatFileRejectionMessage(
  reason: ChatFileRejectionReason,
  types: ChatFileTypes,
): string {
  if (reason === 'size') return `Files must be under ${formatCap(types.maxFileSizeBytes)}.`;
  return `Unsupported file type. Allowed: ${chatFileFormatsLabel(types.extensions)}.`;
}

// One notification for a whole batch, grouped by reason, so dropping several rejected files at
// once does not stack a separate toast per file.
export function chatFileRejectionsMessage(
  rejections: ChatFileRejection[],
  types: ChatFileTypes,
): string {
  const wrongType = rejections.filter(rejection => rejection.reason === 'type');
  const tooLarge = rejections.filter(rejection => rejection.reason === 'size');
  const parts: string[] = [];
  if (wrongType.length) {
    parts.push(
      `Unsupported type: ${filenamesLabel(wrongType)}. Allowed: ${chatFileFormatsLabel(types.extensions)}.`,
    );
  }
  if (tooLarge.length) {
    parts.push(`Over ${formatCap(types.maxFileSizeBytes)}: ${filenamesLabel(tooLarge)}.`);
  }
  return parts.join(' ');
}

// Shown on the attach button so the size cap is discoverable up front rather than only on a
// rejection. Formats are left to the picker's own filter and the rejection message, so the
// tooltip stays short.
export function chatFileAcceptHint(types: ChatFileTypes): string {
  return `Attach a file (max ${formatCap(types.maxFileSizeBytes)}).`;
}

function filenamesLabel(rejections: ChatFileRejection[]): string {
  return rejections.map(rejection => rejection.filename).join(', ');
}

function chatFileFormatsLabel(extensions: string[]): string {
  return extensions.map(extension => extension.slice(1).toUpperCase()).join(', ');
}

export function chatFileButtonAccept(extensions: string[]): string {
  return extensions.join(',');
}

export function chatFileDropzoneAccept(extensions: string[]): Record<string, string[]> {
  const accept: Record<string, string[]> = {};
  for (const extension of extensions) {
    const mimeTypes = CHAT_FILE_MIME_TYPES[extension];
    if (!mimeTypes) {
      // Keyed by extension, the shape Mantine builds from its string[] accept form: dropped files
      // are matched by name, so a type with no MIME entry is still accepted.
      accept[extension] = [];
      continue;
    }
    for (const mimeType of mimeTypes) {
      accept[mimeType] = [...(accept[mimeType] ?? []), extension];
    }
  }
  return accept;
}

// The backend cap is decimal (10_000_000 = 10 MB); 1024-based units would render it as "9.5 MB".
function formatCap(bytes: number): string {
  return `${Math.round(bytes / 100_000) / 10} MB`;
}
